import React from 'react';
import styled from 'styled-components';
import { FireworkWidgetType } from '@contracts';
import { useShowInCountries } from '@hooks/useShowInCountries';

interface FireworkWidgetProps {
  type: FireworkWidgetType;
  id?: string;
  size?: string;
  maxWidth?: string;
  background?: string;
  product?: string;
  showInCountries?: string;
}

const FireworkWidget: React.FC<FireworkWidgetProps> = ({
  type,
  id = '',
  size = 'medium',
  maxWidth = '1000px',
  background = 'transparent',
  product = '',
  showInCountries = ''
}) => {
  const shouldShowInCountries = useShowInCountries(showInCountries);

  if (!shouldShowInCountries) {
    return null;
  }

  const renderContent = () => {
    switch (type) {
      case FireworkWidgetType.Player:
        return (
          <fw-player
            style={{ width: '100%', aspectRatio: '16 / 9' }}
            channel="daye"
            video={id}
            max_videos="1"
            autoplay="true"
            branding="false"
            player_fullscreen_contain="true"
            player_captions="false"
          />
        );
      case FireworkWidgetType.EmbedFeed:
        return (
          <fw-embed-feed
            class="fw-embed-feed needsclick"
            channel="daye"
            {...(id ? { playlist: id } : { skus: product })}
            mode="row"
            open_in="default"
            max_videos="0"
            placement="middle"
            player_placement="bottom-right"
            size={size}
          />
        );
      case FireworkWidgetType.Storyblock:
        return <fw-storyblock channel="daye" playlist={id} mode="pinned" player_placement="bottom-left" autoplay="true" />;
      default:
        return null;
    }
  };

  const content = renderContent();

  if (!content) return null;

  return (
    <Container $background={background}>
      <Inner id="firework-widget" $maxWidth={maxWidth} $type={type}>
        {content}
      </Inner>
    </Container>
  );
};

export default FireworkWidget;

const Container = styled.div<{ $background: string }>`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(props) => props.$background};
`;

const Inner = styled.div<{ $maxWidth: string; $type: string }>`
  max-width: ${(props) => props.$maxWidth};
  width: 100%;

  @media (max-width: ${(props) => props.$maxWidth}) {
    padding: ${(props) => (props.$type === FireworkWidgetType.EmbedFeed ? '0 20px' : '0')};
  }

  &:empty {
    display: none;
  }
`;
